import BasicChart from "components/dashboard/charts/BasicChart";
import { configLineChartData } from "components/dashboard/charts/ChartConfig/configLineChartData";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { ChartData } from "components/dashboard/dataset-window/models/types";
import React, { useState, useEffect } from "react";
import { handleRowReorderEvt } from "utils/utils";
import { IXYMatrixTaskConfigConstantFieldProps } from "./InputFields";
import { Dialog } from "primereact/dialog";

interface XYValueMatrix {
  x: number;
  y: number;
  [key: string]: number;
}

interface XYValueMatrixCustomConfig {
  xLabel: string;
  yLabel: string;
}

interface EditXYValueMatrixProps
  extends IXYMatrixTaskConfigConstantFieldProps<XYValueMatrixCustomConfig> {}

const initialXYValues = [{ x: 1, y: 1 }];

const colorPallet = {
  value: ["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b"],
};

const selectedChartType = "LINE";

const EditXYValueMatrix = (props: EditXYValueMatrixProps) => {
  const xaxisName = props.fieldOptions?.customConfig?.xLabel || "x";
  const yaxisName = props.fieldOptions?.customConfig?.yLabel || "y";
  const xyValuesFromConfig = props.value || initialXYValues;
  const [XYValues, setXYValues] = useState<XYValueMatrix[]>(xyValuesFromConfig);
  const [editXYValueMatrixVisibility, setEditXYValueMatrixVisibility] = useState<boolean>(false);
  const [chartData, setChartData] = useState<ChartData[]>([]);

  useEffect(() => {
    setChartData(newChartData());
  }, [XYValues]);

  const newChartData = () => {
    const newChartDataObject: ChartData[] = [
      {
        id: "",
        unit: yaxisName,
        title: `${xaxisName} vs`,
        keyArr: XYValues.map(item => item.x),
        valueArr: XYValues.map(item => item.y),
      },
    ];
    return newChartDataObject;
  };

  const addXYValues = () => {
    setXYValues([...XYValues, { x: 0, y: 0 }]);
  };

  const handleValuesChange = (rowOptions: XYValueMatrix, value: number) => {
    setXYValues(prevState =>
      prevState.map((item, i) =>
        i === rowOptions.rowIndex ? { ...item, [rowOptions.field]: value } : item
      )
    );

    setChartData(newChartData());
  };

  const removeValues = (options: any) => {
    const updatedValues = XYValues.filter((value, i) => i !== options.rowIndex);
    setXYValues(updatedValues);
  };

  const onRowEditComplete = (e: any) => {
    const datasetsToUpdate = [...XYValues];
    const { newData, index } = e;
    delete newData.index;
    datasetsToUpdate[index] = e.data;
    setXYValues(datasetsToUpdate);
  };

  const handleTableChange = (e: any) => {
    handleRowReorderEvt(XYValues, setXYValues)(e);
    setChartData(newChartData());
  };

  const columnBodyInputTemplate = (rowData: XYValueMatrix, rowOptions: any) => {
    return (
      <InputNumber
        value={rowData[rowOptions.field]}
        style={{ padding: "4px", width: "50%" }}
        onValueChange={(e: any) => handleValuesChange(rowOptions, Number(e.target.value))}
      />
    );
  };

  const removeButton = (options: any) => (
    <Button
      icon="pi pi-trash"
      className="const-del-btn  p-button-rounded p-button-text p-button-danger"
      onClick={() => removeValues(options)}
    />
  );

  const onSave = () => {
    props.onChange?.({ value: XYValues, property: props.property });
    setEditXYValueMatrixVisibility(false);
  };

  const onCancel = () => {
    setEditXYValueMatrixVisibility(false);
    setXYValues(xyValuesFromConfig);
  };

  return (
    <div>
      <div>
        <b>{props.label}</b>
      </div>
      <Button
        className="p-button-outlined p-button-secondary"
        icon="pis"
        label="Edit"
        onClick={() => setEditXYValueMatrixVisibility(true)}
        style={{ width: "100%" }}
        aria-controls="popup_menu"
        aria-haspopup
      />
      <Dialog
        style={{ width: "min(100vw, 800px)", maxHeight: "600px" }}
        header="Edit"
        visible={!!editXYValueMatrixVisibility}
        onHide={onCancel}
        resizable
      >
        <DataTable
          value={XYValues}
          onRowEditComplete={onRowEditComplete}
          editMode="row"
          reorderableRows
          onRowReorder={handleTableChange}
          style={{ paddingBottom: "5px" }}
        >
          <Column rowReorder style={{ width: "3%", padding: "1px" }} />
          <Column
            field="x"
            header={
              <div className="flex align-items-center">
                <div>{xaxisName} value</div>
              </div>
            }
            style={{ padding: "1px" }}
            body={(rowData, options) => columnBodyInputTemplate(rowData, options)}
          />
          <Column
            field="y"
            header={
              <div className="flex align-items-center">
                <div>{yaxisName} value</div>
              </div>
            }
            style={{ padding: "1px" }}
            body={(rowData, options) => columnBodyInputTemplate(rowData, options)}
          />
          <Column
            style={{ width: "40%", textAlign: "right", padding: "1px" }}
            body={(rowData, options) => removeButton(options)}
          />
        </DataTable>
        <div className="flex justify-content-between">
          <div>
            <Button
              className="p-button-outlined p-button-secondary"
              label="Add"
              onClick={addXYValues}
            />
          </div>
          <div className="flex gap-2">
            <Button className="p-button-success" label={"Save"} onClick={onSave} />
            <Button className="p-button-danger" label="Cancel" onClick={onCancel} />
          </div>
        </div>
        <BasicChart
          plotData={configLineChartData(chartData, colorPallet.value)}
          props={{
            xaxisName: xaxisName,
            identifiers: [],
            hideRefresh: true,
            height: 300,
            selectedChartType,
          }}
        />
      </Dialog>
    </div>
  );
};

export default EditXYValueMatrix;
